/**
 * One line of a deal's timeline, as api records it (contracts v3, api part §2.5).
 *
 * The tracker never derives a date from a status: an on-chain step carries the *block* time and
 * the transaction that made it, a platform step carries server time and no hash. `details` holds
 * the money of an outcome in **base units, as strings** — exactly as the contract emitted it, so
 * formatting needs the deal's settlement token and its decimals.
 *
 * Source of truth: api `src/domain/types/deal-step-event/deal-step.enum.ts` and
 * `src/infrastructure/services/evm-escrow/evm-step-events.ts` (which event fills which details).
 */

/** Stable step identifier. The tracker turns it into human copy; the wire value never appears on screen. */
export enum DealStep {
  // --- Delivery: platform steps ---------------------------------------------
  DeliveryOfferCreated = 'delivery.offer_created',
  DeliveryOfferDeclined = 'delivery.offer_declined',
  DeliveryOfferWithdrawn = 'delivery.offer_withdrawn',
  DeliveryOfferExpired = 'delivery.offer_expired',
  DeliveryApproved = 'delivery.approved',
  DeliveryApprovalExpired = 'delivery.approval_expired',
  DeliveryDelivered = 'delivery.delivered',
  DeliveryHandedOver = 'delivery.handed_over',

  // --- Delivery: on-chain steps ---------------------------------------------
  DeliveryEscrowed = 'delivery.escrowed',
  DeliveryPickedUp = 'delivery.picked_up',
  DeliveryCancelled = 'delivery.cancelled',
  DeliveryDisputeRaised = 'delivery.dispute_raised',
  DeliveryResolutionSigned = 'delivery.resolution_signed',
  DeliveryResolved = 'delivery.resolved',
  DeliveryStaleDisputeClaimed = 'delivery.stale_dispute_claimed',

  // --- Rental (PR-8 renders these; listed so the type is the whole enum) -----
  RentalBookingCreated = 'rental.booking_created',
  RentalBookingExpired = 'rental.booking_expired',
  RentalPaid = 'rental.paid',
  RentalPickupConfirmed = 'rental.pickup_confirmed',
  RentalReadyForPickup = 'rental.ready_for_pickup',
  RentalStarted = 'rental.started',
  RentalOverdueCharged = 'rental.overdue_charged',
  RentalDepositToppedUp = 'rental.deposit_topped_up',
  RentalExtensionRequested = 'rental.extension_requested',
  RentalExtensionDeclined = 'rental.extension_declined',
  RentalExtended = 'rental.extended',
  RentalVehicleReturned = 'rental.vehicle_returned',
  RentalDefectReported = 'rental.defect_reported',
  RentalDefectApproved = 'rental.defect_approved',
  RentalDefectDisputed = 'rental.defect_disputed',
  RentalSplitProposed = 'rental.split_proposed',
  RentalDisputeResolved = 'rental.dispute_resolved',
  RentalDepositReleased = 'rental.deposit_released',
  RentalStaleDepositClaimed = 'rental.stale_deposit_claimed',
  RentalCancelled = 'rental.cancelled',
  RentalExpiredBookingCancelled = 'rental.expired_booking_cancelled',
}

/** A recorded step. `txHash` is present exactly when a transaction carried it. */
export interface ApiDealStepEvent {
  step: DealStep;
  /** Block time for on-chain steps, server time for platform steps. ISO string. */
  occurredAt: string;
  txHash: string | null;
  /** Money and context of the outcome; amounts are base-unit strings. */
  details: Record<string, unknown> | null;
}

/**
 * The buyer's/dealer's vote in a delivery dispute — the contract's own `Vote` enum
 * (onchain part §3.1), mirrored by api into the deal projection.
 */
export enum DeliveryVote {
  None = 0,
  Refund = 1,
  Payout = 2,
}
